/**
 * Els casos vistos des d'una reserva — `plans/reservation-context-and-use-cases.md` §F3.5.
 *
 * Tres decisions petites que valen un fitxer propi perquè es puguin provar sense muntar
 * la fitxa sencera: quines reserves porta un cas nou, com es resumeix un cas en una línia
 * i quina és l'adreça de `/casos` filtrat per una reserva.
 */

/** Un número de reserva de Beds24: exactament 8 xifres, com a l'URL i al formulari. */
const BEDS24_ID = /^\d{8}$/;

export const CASOS_PATH = '/casos';

/**
 * Amb quines reserves arrenca el formulari d'un cas obert des d'una fitxa.
 *
 * Si la reserva forma part d'un grup de Beds24, hi entren **totes** les germanes: el que
 * ha passat en una habitació d'un grup normalment ha passat al grup, i afegir-les a mà
 * una per una era garantia que no es fes. Fora del grup, només la del focus.
 *
 * La del focus va sempre primera i no es pot perdre pel camí, encara que el backend
 * torni el grup en un altre ordre o no l'hi inclogui.
 */
export function caseBeds24IdsForReservation(
  beds24Id: string | null | undefined,
  groupBeds24Ids: string[] = [],
): string[] {
  if (!beds24Id || !BEDS24_ID.test(beds24Id)) return [];

  const siblings = groupBeds24Ids.filter((id) => BEDS24_ID.test(id));
  return [...new Set([beds24Id, ...siblings])];
}

/**
 * Les primeres paraules d'un cas, per a la llista.
 *
 * Talla per paraula i no per lletra: un cas retallat a mig mot es llegeix com un error de
 * la pàgina. Els salts de línia passen a espais perquè una línia de la llista sigui una
 * línia de veritat.
 */
export function caseExcerpt(text: string, maxChars = 90): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= maxChars) return flat;

  const cut = flat.slice(0, maxChars);
  // Si el tall cau just al final d'una paraula, ja hi som: retrocedir fins a l'espai
  // anterior perdria una paraula sencera per no res.
  const lastSpace = flat[maxChars] === ' ' ? -1 : cut.lastIndexOf(' ');
  // Sense cap espai a la finestra (una paraula llarguíssima) val més tallar-la que
  // tornar la cadena buida.
  const head = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return `${head.replace(/[.,;:]$/, '')}…`;
}

/**
 * `/casos?beds24Id=92725627` — el camí de tornada del que fa `buildReservationPath`.
 *
 * El filtre va per `beds24Id` i no pel `cuid` intern pel mateix motiu que l'URL de la
 * fitxa: és el número que la gent llegeix i que es pot enviar per WhatsApp.
 */
export function casosPathForBeds24Id(beds24Id: string | null | undefined): string {
  if (!beds24Id || !BEDS24_ID.test(beds24Id)) return CASOS_PATH;
  return `${CASOS_PATH}?beds24Id=${beds24Id}`;
}
